/**
 * <BrowserNavigateBlock> — render for the upstream `browser` tool's
 * `navigate` sub-action.
 *
 * The native OpenClaw browser tool is a single tool with 17 sub-actions
 * keyed by an `action` parameter (extensions/browser/src/browser-tool.schema.ts:19-37).
 * This component is dispatched by `<ToolUseBlock>` when
 * `block.name === "browser"` AND `block.input.action === "navigate"`,
 * showing the destination URL as a small badge. The pixel-level
 * result lives in the Lexiun Browser pane (the supervisor's noVNC
 * paint) — this badge is the in-chat trail.
 *
 * Other browser sub-actions (click, type, snapshot, …) fall back to
 * `<CollapsedToolJson>` since their inputs are less self-explanatory
 * at a glance.
 *
 * Companion to `useBrowserActivityFromAgent` (toast nudge for the same
 * navigate sub-action).
 */
import { Globe } from "lucide-react";
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";

interface BrowserNavigateBlockProps {
  block: ToolUseBlockType;
}

function readUrl(input: unknown): string | null {
  if (!input || typeof input !== "object") return null;
  const r = input as Record<string, unknown>;
  return typeof r.url === "string" && r.url ? r.url : null;
}

function displayUrl(url: string): string {
  // Trim the protocol for visual density; full URL stays in the title
  // attribute so power users can hover to see the literal value.
  if (url.length <= 70) return url.replace(/^https?:\/\//, "");
  try {
    const parsed = new URL(url);
    return `${parsed.host}/...`;
  } catch {
    return `${url.slice(0, 67)}...`;
  }
}

export function BrowserNavigateBlock({ block }: BrowserNavigateBlockProps) {
  const url = readUrl(block.input);
  return (
    <div
      data-testid="browser-navigate-block"
      data-tool-name="browser"
      data-tool-action="navigate"
      title={url ?? undefined}
      className="my-1 inline-flex items-center gap-1.5 rounded border border-cream-300 bg-cream-100/30 px-2 py-1 text-caption text-coffee-700 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-300"
    >
      <Globe size={12} />
      <span>Navigating</span>
      {url && <span className="font-mono">{displayUrl(url)}</span>}
    </div>
  );
}
